import { auth } from '@/lib/firebase';
import { api } from '@/lib/api';
import { ActivityTracker } from '@/utils/activityTracker';

// Service to fetch and manage floorplan data
export interface FloorplanSpace {
  id: string;
  name: string;
  type: 'floor' | 'space' | 'zone';
  parentId: string | null;
  path: string;
  level: number;
  order: number;
  floorplan?: any;
  metadata?: any;
  settings?: any;
  ui?: any;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  Devices: any[];
  Scenes: any[];
}

export interface Floorplan {
  id: number;
  name: string;
  type?: string;
  spaces?: FloorplanSpace[];
  imageUrl?: string;
}

export interface FloorplanDevice {
  id: string;
  name: string;
  type: string;
  areaId: string;
  status: string;
  position: { x: number; y: number };
  powerConsumption: number;
  lastUpdated: string;
  capabilities: string[];
  state?: {
    power?: boolean;
    dimmer?: number;
    temperature?: number;
  };
}

// Convert backend space data to frontend floor format
export function convertSpacesToFloors(spaces: FloorplanSpace[]) {
  const floors: any[] = [];
  
  // Accept floors even when they sit under a building node (building -> floor -> zone/space).
  const floorSpaces = spaces.filter(space => space.type === 'floor');
  
  floorSpaces.forEach(floorSpace => {
    const areas: any[] = [];

    // Support both hierarchies used in this codebase:
    // 1. floor -> space -> zone
    // 2. floor -> zone -> space -> zone
    const directSpaceAreas = spaces.filter(
      space => space.parentId === floorSpace.id && space.type === 'space'
    );

    const floorZones = spaces.filter(
      space => space.parentId === floorSpace.id && space.type === 'zone'
    );

    const nestedSpaceAreas = floorZones.flatMap(zone =>
      spaces.filter(space => space.parentId === zone.id && space.type === 'space')
    );

    const areaSpaces = [...directSpaceAreas, ...nestedSpaceAreas].filter(
      (space, index, list) => list.findIndex(candidate => candidate.id === space.id) === index
    );

    areaSpaces.forEach(areaSpace => {
      const zoneSpaces = spaces.filter(space =>
        space.parentId === areaSpace.id && space.type === 'zone'
      );

      areas.push({
        id: areaSpace.id,
        name: areaSpace.name,
        zones: zoneSpaces.map(zone => ({
          id: zone.id,
          name: zone.name,
          lights: 0
        }))
      });
    });
    
    floors.push({
      id: floorSpace.id,
      name: floorSpace.name,
      areas: areas,
      sensors: [],
      scenes: [],
      receptacles: 0,
      energy: { current: 0, avg: 0 },
      alerts: [],
      shades: 'Open',
      roomControls: []
    });
  });
  
  return floors;
}

export async function fetchLatestFloorplan(): Promise<any[]> {
  try {
    const response = await api.get('/api/floorplans');
    
    const floorplans: Floorplan[] = response.data;
    console.log(floorplans,"floorplans");
    
    // Find the latest hierarchy floorplan
    const hierarchyFloorplans = floorplans.filter(fp => fp.type === 'hierarchy' && fp.spaces);
    if (hierarchyFloorplans.length === 0) {
      return []; // Return empty array if no hierarchy floorplans
    }
    console.log("hierarchyFloorplans",hierarchyFloorplans,"hierarchyFloorplans");
    
    // Get the earliest one (lowest ID) to avoid test data
    const latestFloorplan = hierarchyFloorplans.reduce((earliest, current) => 
      current.id < earliest.id ? current : earliest
    );
    console.log("latestFloorplan",latestFloorplan);
    
    
    if (latestFloorplan.spaces) {
      return convertSpacesToFloors(latestFloorplan.spaces);
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching floorplan:', error);
    return []; // Return empty array on error
  }
}

// Add device to floorplan
export async function addDeviceToFloorplan(device: FloorplanDevice): Promise<boolean> {
  try {
    const response = await api.post(`/api/spaces/${device.areaId}/devices`, device);
    
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Failed to add device to floorplan: ${response.statusText}`);
    }
    
    // Track device addition activity
    ActivityTracker.trackDeviceAdded(device.name, device.areaId);
    
    return true;
  } catch (error) {
    console.error('Error adding device to floorplan:', error);
    ActivityTracker.trackSystemError(`Failed to add device ${device.name} to floorplan: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return false;
  }
}

// Update device in floorplan
export async function updateDeviceInFloorplan(device: FloorplanDevice): Promise<boolean> {
  try {
    const response = await api.put(`/api/spaces/${device.areaId}/devices`, {
      deviceId: device.id,
      deviceType: device.type,
      deviceName: device.name,
      deviceState: device.state
    });

    if (response.status !== 200) {
      throw new Error(`Failed to update device in floorplan: ${response.statusText}`);
    }

    // Track device update activity
    ActivityTracker.trackActivity('device', `Device ${device.name} updated in floorplan`, device.areaId, { deviceType: device.type });

    return true;
  } catch (error) {
    console.error('Error updating device in floorplan:', error);
    ActivityTracker.trackSystemError(`Failed to update device ${device.name} in floorplan: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return false;
  }
} 